import type { OutcomeTier } from '../mission/outcome';
import type { IncidentKind } from '../mission/incident';

/**
 * Der Preis der Wahrheit.
 *
 * Das Spiel hat eine harte Mechanik im Kern: Harte Beweise liegen nur dort,
 * wo niemand mehr zu retten war. Wer den Fall loesen will, muss Notrufe
 * verstreichen lassen oder zu spaet kommen. Bis hierher stand das nur im
 * Code — sichtbar wurde es erst im Finale als eine Zeile „gerettet/verloren“.
 *
 * Dieses Modul fuehrt Buch darueber, *welcher* Hinweis *welche* Toten
 * gekostet hat, und haelt eine Mannschaft, die das merkt. Die Moral ist
 * bewusst an genau ein System gekoppelt — die Kosten von Entscheidungen
 * unterwegs —, wie die Hintergruende auch: Eine Zahl, die ueberall ein wenig
 * wirkt, liest niemand.
 *
 * Rein und deterministisch, ohne Zufall: Dieselben Einsaetze ergeben dieselbe
 * Bilanz.
 */

export interface TollEntry {
  readonly incidentId: string;
  readonly vesselName: string;
  readonly kind: IncidentKind;
  readonly tier: OutcomeTier | 'lapsed';
  readonly souls: number;
  readonly rescued: number;
  readonly lost: number;
  /** Beat-Indizes der Hinweise, die dieser Einsatz eingebracht hat. */
  readonly clueBeats: readonly number[];
  /**
   * Waren diese Toten vermeidbar?
   *
   * Ja, wenn das Rettungsfenster beim Auslaufen noch offen stand — dann hat
   * man zu langsam gefahren, einen Umweg genommen oder anderswo gehalten. Und
   * ja, wenn ein Notruf ungenutzt verstrich, waehrend ein Fahrzeug frei war.
   */
  readonly avoidable: boolean;
  readonly atHours: number;
}

export interface TollSave {
  readonly morale: number;
  readonly entries: readonly TollEntry[];
}

/** Voreinstellung: eine erfahrene Mannschaft, die schon einiges gesehen hat. */
export const STARTING_MORALE = 70;
/** Mehr als das holt die Ruhe an der Station nicht zurueck. */
const REST_CEILING = 75;

/** Voreinstellung: ab hier verweigert die Mannschaft Umwege fuer die Akte. */
export const MUTINY_THRESHOLD = 25;

/**
 * Wie schwer Verluste wiegen und wann die Mannschaft sich verweigert — beides
 * kommt vom Schwierigkeitsgrad.
 */
export interface TollRules {
  readonly moraleLoss: number;
  readonly mutinyThreshold: number;
}

const DEFAULT_RULES: TollRules = { moraleLoss: 1, mutinyThreshold: MUTINY_THRESHOLD };

export class Toll {
  private moraleValue = STARTING_MORALE;
  private readonly list: TollEntry[] = [];

  constructor(private readonly rules: TollRules = DEFAULT_RULES) {}

  get morale(): number {
    return this.moraleValue;
  }

  get entries(): readonly TollEntry[] {
    return this.list;
  }

  get rescued(): number {
    return this.list.reduce((sum, entry) => sum + entry.rescued, 0);
  }

  get lost(): number {
    return this.list.reduce((sum, entry) => sum + entry.lost, 0);
  }

  /** Tote, die haetten gerettet werden koennen. */
  get avoidableLost(): number {
    return this.list.filter((e) => e.avoidable).reduce((sum, e) => sum + e.lost, 0);
  }

  /** Ein Wort fuer die Stimmung an Bord — als Uebersetzungsschluessel. */
  get moodKey(): string {
    if (this.moraleValue >= 65) return 'toll.mood.steady';
    if (this.moraleValue >= 45) return 'toll.mood.strained';
    if (this.moraleValue >= this.rules.mutinyThreshold) return 'toll.mood.grim';
    return 'toll.mood.breaking';
  }

  /**
   * Faktor auf die Kosten von Entscheidungen unterwegs.
   *
   * Eine muede, verbitterte Mannschaft arbeitet langsamer und unsauberer:
   * Umwege dauern laenger, Reparaturen kosten mehr. Oberhalb von 60 gibt es
   * keinen Aufschlag, bei null Moral gut das Siebenviertelfache.
   *
   * Die Schwelle lag frueher bei 50 und der Aufschlag war halb so steil — in
   * Testlaeufen blieb die Moral damit fast immer darueber, und das System
   * war da, ohne je zu wirken.
   */
  get eventCostFactor(): number {
    return 1 + Math.max(0, (60 - this.moraleValue) / 80);
  }

  /** Verweigert die Mannschaft Umwege, die nur der Akte dienen? */
  get refusesDetours(): boolean {
    return this.moraleValue < this.rules.mutinyThreshold;
  }

  /** Welcher Einsatz hat diesen Hinweis eingebracht? */
  entryForBeat(beatIndex: number): TollEntry | null {
    return this.list.find((entry) => entry.clueBeats.includes(beatIndex)) ?? null;
  }

  /**
   * Ein abgeschlossener Einsatz.
   *
   * Rettungen heben die Stimmung, Tote druecken sie — vermeidbare doppelt.
   * Lange Fahrten zehren zusaetzlich.
   *
   * Rettungen heben bewusst weniger, als Verluste senken: Eine Mannschaft
   * gewoehnt sich an Erfolge schneller als an Tote. Frueher war es
   * umgekehrt, und wer viel rettete, hatte nach wenigen Einsaetzen eine
   * Moral, die durch nichts mehr zu erschuettern war.
   */
  recordMission(entry: Omit<TollEntry, 'lost' | 'rescued'>, voyageHours: number): TollEntry {
    const rescued = entry.tier === 'rescue' ? entry.souls : 0;
    const full: TollEntry = { ...entry, rescued, lost: entry.souls - rescued };
    this.list.push(full);

    if (entry.tier === 'rescue') {
      this.shift(4 + Math.min(3, entry.souls / 30));
    } else if (entry.souls > 0) {
      const weight = entry.avoidable ? 2 : 1;
      this.lose(weight * Math.min(10, 3 + entry.souls / 8));
    }
    this.lose(Math.max(0, voyageHours - 24) / 36);
    return full;
  }

  /** Ein Notruf, der verstrich, ohne dass jemand hinfuhr. */
  recordLapsed(params: {
    incidentId: string;
    vesselName: string;
    kind: IncidentKind;
    souls: number;
    atHours: number;
    avoidable: boolean;
  }): void {
    this.list.push({
      ...params,
      tier: 'lapsed',
      rescued: 0,
      lost: params.souls,
      clueBeats: [],
    });
    this.lose((params.avoidable ? 2 : 1) * Math.min(8, 2 + params.souls / 12));
  }

  /** Unterwegs Menschen aufgenommen — das hilft. */
  recordEnRouteRescue(souls: number): void {
    if (souls <= 0) return;
    this.shift(2 + Math.min(3, souls / 15));
  }

  /** Ein Fahrzeug liegt fest und muss geschleppt werden. */
  recordBreakdown(): void {
    this.lose(6);
  }

  /**
   * Ruhe an der Station.
   *
   * Je acht Stunden an Land ein Punkt, bis zu einer Decke — Ruhe heilt, aber
   * nicht alles.
   */
  rest(hours: number): void {
    if (hours <= 0 || this.moraleValue >= REST_CEILING) return;
    this.moraleValue = Math.min(REST_CEILING, this.moraleValue + hours / 8);
  }

  /** Ein Verlust, verstaerkt oder gemildert durch den Schwierigkeitsgrad. */
  private lose(amount: number): void {
    if (amount > 0) this.shift(-amount * this.rules.moraleLoss);
  }

  /** Nachtraegliche Zuordnung eines Hinweises, der unterwegs anfiel. */
  attachClue(incidentId: string, beatIndex: number): void {
    const index = this.list.findIndex((entry) => entry.incidentId === incidentId);
    if (index < 0) return;
    const entry = this.list[index] as TollEntry;
    this.list[index] = { ...entry, clueBeats: [...entry.clueBeats, beatIndex] };
  }

  private shift(delta: number): void {
    this.moraleValue = Math.max(0, Math.min(100, this.moraleValue + delta));
  }

  serialise(): TollSave {
    return { morale: this.moraleValue, entries: [...this.list] };
  }

  static restore(save: TollSave | undefined, rules: TollRules = DEFAULT_RULES): Toll {
    const toll = new Toll(rules);
    if (save === undefined) return toll;
    toll.moraleValue = Math.max(0, Math.min(100, save.morale));
    toll.list.push(...save.entries);
    return toll;
  }
}

/**
 * Das Urteil im Finale: Wie viele Tote kostete ein harter Hinweis im Schnitt?
 *
 * Als Schluessel fuer einen Satz, nicht als Zahl — der Spieler soll lesen,
 * was er getan hat, nicht einen Punktestand.
 */
export function verdictKey(toll: Toll, hardClues: number): string {
  if (toll.lost === 0) return 'toll.verdict.clean';
  if (toll.rescued >= toll.lost * 2) return 'toll.verdict.balanced';
  const perClue = hardClues > 0 ? toll.avoidableLost / hardClues : toll.avoidableLost;
  if (perClue >= 15) return 'toll.verdict.ruthless';
  if (toll.avoidableLost > 0) return 'toll.verdict.costly';
  return 'toll.verdict.unavoidable';
}
